/**
 * Cart state stored in localStorage. One cart per shop (cart is cleared if user
 * adds items from a different shop).
 */
import { useEffect, useState } from 'react';

const KEY = 'rl_cart';

export interface CartItem {
  product_id: string;
  quantity: number;
  name: string;
  price_cents: number;
  photo_url: string | null;
  category: string | null;
}

export interface Cart {
  shop_slug: string;
  shop_name: string;
  items: CartItem[];
}

function readCart(): Cart | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Cart;
  } catch {
    return null;
  }
}

function writeCart(cart: Cart | null): void {
  if (cart && cart.items.length > 0) localStorage.setItem(KEY, JSON.stringify(cart));
  else localStorage.removeItem(KEY);
  window.dispatchEvent(new Event('rl:cart'));
}

export function useCart(): [Cart | null, CartActions] {
  const [cart, setCart] = useState<Cart | null>(() => readCart());

  useEffect(() => {
    const onChange = () => setCart(readCart());
    window.addEventListener('rl:cart', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('rl:cart', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  return [cart, actions];
}

export interface CartActions {
  addItem(shop: { slug: string; name: string }, item: Omit<CartItem, 'quantity'>, quantity?: number): { ok: true } | { ok: false; reason: 'different_shop'; existingShop: string };
  setQuantity(product_id: string, quantity: number): void;
  clear(): void;
}

const actions: CartActions = {
  addItem(shop, item, quantity = 1) {
    const cur = readCart();
    if (cur && cur.shop_slug !== shop.slug) {
      return { ok: false, reason: 'different_shop', existingShop: cur.shop_name };
    }
    const base: Cart = cur ?? { shop_slug: shop.slug, shop_name: shop.name, items: [] };
    const existing = base.items.find((i) => i.product_id === item.product_id);
    if (existing) existing.quantity += quantity;
    else base.items.push({ ...item, quantity });
    writeCart(base);
    return { ok: true };
  },
  setQuantity(product_id, quantity) {
    const cur = readCart();
    if (!cur) return;
    if (quantity <= 0) cur.items = cur.items.filter((i) => i.product_id !== product_id);
    else {
      const existing = cur.items.find((i) => i.product_id === product_id);
      if (existing) existing.quantity = quantity;
    }
    writeCart(cur.items.length > 0 ? cur : null);
  },
  clear() {
    writeCart(null);
  },
};

export function cartTotalCents(cart: Cart | null): number {
  if (!cart) return 0;
  return cart.items.reduce((s, i) => s + i.price_cents * i.quantity, 0);
}

export function cartCount(cart: Cart | null): number {
  if (!cart) return 0;
  return cart.items.reduce((s, i) => s + i.quantity, 0);
}
